import React from "react";
import { motion } from "framer-motion";
 import { Link } from "react-router-dom"; // Removed Link for isolated component context
 import Navbar from "../components/Navbar"; // Removed due to compilation errors; integrate globally if needed

const cardData = [
  {
    title: "Workforce Solutions",
    description: "Optimize your team's potential with tailored services for managing both permanent and contingent workforce effectively.",
    accentColor: "indigo-600", // Accent color for highlights
    icon: "👥",
  },
  {
    title: "Advanced Analytics",
    description: "Navigate complex data challenges and unlock actionable insights with our cutting-edge analytics solutions.",
    accentColor: "purple-600",
    icon: "�",
  },
  {
    title: "Talent on Demand",
    description: "Access a curated pool of highly skilled professionals to seamlessly integrate into your projects, precisely when you need them.",
    accentColor: "teal-600",
    icon: "⭐",
  },
  {
    title: "Digital Transformation",
    description: "Lead your industry forward by embracing innovative digital strategies and technologies for sustained growth.",
    accentColor: "blue-600",
    icon: "🚀",
  },
  {
    title: "Strategic Consulting",
    description: "Leverage expert guidance to refine your business strategies, enhance operational efficiency, and achieve long-term objectives.",
    accentColor: "orange-600",
    icon: "💡",
  },
];

const Industries = () => {
  // Framer Motion variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut", delay: 0.2 } },
  };

  const cardContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardItemVariants = {
    hidden: { y: 30, opacity: 0, scale: 0.9 },
    visible: { y: 0, opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <>
      <Navbar />
      {/* Navbar removed; ensure it's handled globally or re-integrated if necessary */}
      <section className="relative px-4 py-20 bg-gradient-to-br from-white to-blue-50 min-h-screen flex items-center justify-center font-inter overflow-hidden">
        {/* Background Gradients and Patterns */}
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/30 via-transparent to-transparent opacity-50"></div>
        <div className="absolute inset-0 z-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cuc2Nvb3BzLmlvIj48ZyBmaWxsPSJub25lIiBmaWxsLW9wYWNpdHk9IjAuMTUiIHN0cm9rZT0iIzc0NzcyQiIgc3Ryb2tlLXdpZHRoPSIxIj48cGF0aCBkPSJNMCAxMGw1OCAwVjBMMzAgMzBMNjAgMzBMNjAgNjBIMlA1OSAxMFoiLz48L2c+PC9zdmc+')] opacity-10 scale-150 animate-pulse-slow"></div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Section Heading */}
          <motion.p
            className="text-indigo-600 font-semibold uppercase tracking-wider mb-2 text-sm md:text-base"
            initial="hidden"
            animate="visible"
            variants={subtitleVariants}
          >
            Achieve Phenomenal Breakthrough
          </motion.p>
          <motion.h2
            className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-16"
            initial="hidden"
            animate="visible"
            variants={titleVariants}
          >
            Transform Your Business with <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Tailored Solutions
            </span>
          </motion.h2>

          {/* Cards Grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={cardContainerVariants}
          >
            {cardData.map((card, index) => (
              <motion.div
                key={index}
                className={`bg-white p-8 rounded-2xl shadow-xl border border-gray-100
                           flex flex-col items-center text-center group
                           transition-all duration-300 ease-in-out
                           hover:shadow-2xl hover:-translate-y-2 hover:border-${card.accentColor} relative overflow-hidden`}
                variants={cardItemVariants}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Accent wave/blob */}
                <div className={`absolute top-0 left-0 w-24 h-24 rounded-full -translate-x-1/2 -translate-y-1/2 bg-${card.accentColor} opacity-10 blur-xl group-hover:opacity-20 transition-opacity duration-300`}></div>
                <div className={`absolute bottom-0 right-0 w-24 h-24 rounded-full translate-x-1/2 translate-y-1/2 bg-${card.accentColor} opacity-10 blur-xl group-hover:opacity-20 transition-opacity duration-300`}></div>


                <div
                  className={`w-20 h-20 rounded-full flex items-center justify-center text-4xl mb-6 relative z-10
                              bg-gradient-to-br from-${card.accentColor.replace('-600', '-400')} to-${card.accentColor} text-white
                              group-hover:from-white group-hover:to-white group-hover:text-${card.accentColor} transition-all duration-300 ease-in-out border-2 border-transparent group-hover:border-${card.accentColor}`}
                >
                  {card.icon}
                </div>
                <h3 className={`text-2xl font-bold text-gray-900 mb-3 group-hover:text-${card.accentColor} transition-colors duration-300 relative z-10`}>
                  {card.title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed mb-6 relative z-10">
                  {card.description}
                </p>
                <motion.button
                  className={`inline-flex items-center justify-center px-6 py-3 border-2 border-${card.accentColor} text-base font-medium rounded-full
                             text-${card.accentColor} bg-white hover:text-white hover:bg-${card.accentColor} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${card.accentColor}
                             transition-all duration-300 ease-in-out transform hover:scale-105 relative z-10`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Explore Now
                  <svg
                    className="ml-2 -mr-1 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 000 1.414l-5 5a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                    <path
                      fillRule="evenodd"
                      d="M4.293 15.707a1 1 0 010-1.414L8.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 f0 000 1.414l-5 5a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Industries;
